import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Employee } from "@shared/schema";
import type { EmergencyContact } from "@shared/schema";
import { toast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { formatPhoneInputValue } from "@/lib/phone";
import { AddressFields } from "@/components/forms/AddressFields";
import { ProfilePictureUpload } from "@/components/ui/profile-picture-upload";
import { EmergencyContactModal } from "@/components/forms/EmergencyContactModal";
import { Heart, Plus, Edit, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const profileSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Invalid email address"),
  phone: z.string().optional(),
  dateOfBirth: z.string().min(1, "Date of birth is required"),
  sex: z.string().min(1, "Sex is required"),
  maritalStatus: z.string().min(1, "Marital status is required"),
  address1: z.string().min(1, "Address is required"),
  address2: z.string().optional(),
  city: z.string().min(1, "City is required"),
  state: z.string().min(1, "State is required"),
  zipCode: z.string().min(1, "Zip code is required"),
  county: z.string().optional(),
  country: z.string().optional(),
  profilePicture: z.string().optional(),
});

type ProfileFormData = z.infer<typeof profileSchema>;

interface ProfileSetupScreenProps {
  employee: Employee;
  onComplete: () => void;
  /**
   * Optional skip handler. When omitted, no "Skip for Now" button renders
   * and the user must complete the form to advance.
   */
  onSkip?: () => void;
}

export function ProfileSetupScreen({
  employee,
  onComplete,
  onSkip,
}: ProfileSetupScreenProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEmergencyContactDialog, setShowEmergencyContactDialog] = useState(false);
  const [editingEmergencyContact, setEditingEmergencyContact] = useState<EmergencyContact | null>(null);

  const { data: emergencyContactsRaw = [], isLoading: emergencyLoading } = useQuery({
    queryKey: ["/api/employees/me/emergency-contacts"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/employees/me/emergency-contacts");
      return response.json();
    },
  });
  const emergencyContacts: EmergencyContact[] = Array.isArray(emergencyContactsRaw) ? emergencyContactsRaw : [];

  const addEmergencyContactMutation = useMutation({
    mutationFn: async (contactData: any) => {
      const response = await apiRequest("POST", "/api/employees/me/emergency-contacts", contactData);
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Emergency Contact Added", description: "Contact saved successfully.", variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["/api/employees/me/emergency-contacts"] });
      setShowEmergencyContactDialog(false);
      setEditingEmergencyContact(null);
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to add emergency contact.", variant: "destructive" });
    },
  });

  const updateEmergencyContactMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const response = await apiRequest("PATCH", `/api/emergency-contacts/${id}`, data);
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Emergency Contact Updated", description: "Contact updated successfully.", variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["/api/employees/me/emergency-contacts"] });
      setShowEmergencyContactDialog(false);
      setEditingEmergencyContact(null);
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update emergency contact.", variant: "destructive" });
    },
  });

  const deleteEmergencyContactMutation = useMutation({
    mutationFn: async (id: number) => {
      return await apiRequest("DELETE", `/api/emergency-contacts/${id}`);
    },
    onSuccess: () => {
      toast({ title: "Emergency Contact Deleted", description: "Contact removed.", variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["/api/employees/me/emergency-contacts"] });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to delete emergency contact.", variant: "destructive" });
    },
  });

  const handleEmergencyContactSubmit = (data: any) => {
    if (editingEmergencyContact?.id) {
      updateEmergencyContactMutation.mutate({ id: editingEmergencyContact.id, data });
    } else {
      addEmergencyContactMutation.mutate(data);
    }
  };

  const handleEditEmergencyContact = (contact: any) => {
    setEditingEmergencyContact(contact);
    setShowEmergencyContactDialog(true);
  };

  const handleDeleteEmergencyContact = (id: number) => {
    if (confirm("Are you sure you want to delete this emergency contact?")) {
      deleteEmergencyContactMutation.mutate(id);
    }
  };

  const form = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: employee.firstName || "",
      lastName: employee.lastName || "",
      email: employee.email || "",
      phone: employee.phone || "",
      dateOfBirth: employee.dateOfBirth
        ? new Date(employee.dateOfBirth).toISOString().split("T")[0]
        : "",
      sex: employee.sex || "",
      maritalStatus: employee.maritalStatus || "",
      address1: employee.address1 || "",
      address2: employee.address2 || "",
      city: employee.city || "",
      state: employee.state || "",
      zipCode: employee.zipCode || "",
      county: employee.county || "",
      country: employee.country || "United States",
      profilePicture: employee.profilePicture || "",
    },
  });

  // Reset form when employee data loads/changes
  useEffect(() => {
    if (employee) {
      form.reset({
        firstName: employee.firstName || "",
        lastName: employee.lastName || "",
        email: employee.email || "",
        phone: employee.phone || "",
        dateOfBirth: employee.dateOfBirth
          ? new Date(employee.dateOfBirth).toISOString().split("T")[0]
          : "",
        sex: employee.sex || "",
        maritalStatus: employee.maritalStatus || "",
        address1: employee.address1 || "",
        address2: employee.address2 || "",
        city: employee.city || "",
        state: employee.state || "",
        zipCode: employee.zipCode || "",
        county: employee.county || "",
        country: employee.country || "United States",
        profilePicture: employee.profilePicture || "",
      });
    }
  }, [employee, form]);

  const onSubmit = async (data: ProfileFormData) => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`/api/employees/me`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const text = await response.text();
        let msg = text || response.statusText;
        try {
          const json = JSON.parse(text);
          msg = json.message || json.error || msg;
        } catch {}
        throw new Error(msg);
      }

      queryClient.invalidateQueries({ queryKey: ["/api/employees/me"] });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });

      toast({
        title: "Profile updated!",
        description: "Your information has been saved.",
        variant: "success",
      });

      onComplete();
    } catch (error: any) {
      console.error("Profile update error:", error);
      toast({
        title: "Update failed",
        description: error?.message || "Please try again or skip for now.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <ProfilePictureUpload
          currentPictureUrl={employee.profilePicture}
          employeeId={employee.id}
          onUploadSuccess={(url) => {
            form.setValue("profilePicture", url);
            queryClient.invalidateQueries({ queryKey: ["/api/employees/me"] });
          }}
        />
        <p className="text-sm text-muted-foreground mt-2">
          Upload a profile picture (optional)
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h4 className="font-medium">Basic Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email *</FormLabel>
                    <FormControl>
                      <Input type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        onChange={(e) =>
                          field.onChange(formatPhoneInputValue(e.target.value))
                        }
                        placeholder="(123) 456-7890"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="dateOfBirth"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Date of Birth *</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="sex"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Sex *</FormLabel>
                    <Select
                      value={field.value || undefined}
                      onValueChange={field.onChange}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="maritalStatus"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Marital Status *</FormLabel>
                    <Select
                      value={field.value || undefined}
                      onValueChange={field.onChange}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="single">Single</SelectItem>
                        <SelectItem value="married">Married</SelectItem>
                        <SelectItem value="divorced">Divorced</SelectItem>
                        <SelectItem value="widowed">Widowed</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Address Information */}
          <div className="space-y-4">
            <h4 className="font-medium">Address</h4>
            <AddressFields form={form} />
          </div>

          {/* Emergency Contact */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium flex items-center gap-2">
                <Heart className="h-4 w-4" />
                Emergency Contacts
              </h4>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditingEmergencyContact(null);
                  setShowEmergencyContactDialog(true);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Contact
              </Button>
            </div>
            <div className="space-y-3">
              {emergencyLoading && <p className="text-sm text-muted-foreground">Loading...</p>}
              {!emergencyLoading && emergencyContacts.map((contact: any) => (
                <div key={contact.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <div className="font-medium flex items-center gap-2">
                      {contact.name}
                      {contact.isPrimary && <Badge variant="default">Primary</Badge>}
                    </div>
                    <div className="text-sm text-muted-foreground">{contact.relationship} • {contact.phone}</div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button type="button" variant="ghost" size="sm" onClick={() => handleEditEmergencyContact(contact)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => handleDeleteEmergencyContact(contact.id)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
              {!emergencyLoading && emergencyContacts.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">No emergency contacts added yet. Click "Add Contact" to get started.</p>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className={`flex pt-4 ${onSkip ? "justify-between" : "justify-end"}`}>
            {onSkip && (
              <Button type="button" variant="outline" onClick={onSkip}>
                Skip for Now
              </Button>
            )}
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Save & Continue"}
            </Button>
          </div>
        </form>
      </Form>

      <EmergencyContactModal
        open={showEmergencyContactDialog}
        onOpenChange={(open) => {
          setShowEmergencyContactDialog(open);
          if (!open) setEditingEmergencyContact(null);
        }}
        onSubmit={handleEmergencyContactSubmit}
        editingContact={editingEmergencyContact}
        employeeName={`${employee.firstName} ${employee.lastName}`}
        employeeAddress={{
          address1: employee.address1 || "",
          address2: employee.address2 || "",
          city: employee.city || "",
          state: employee.state || "",
          zipCode: employee.zipCode || "",
          county: employee.county || "",
          country: employee.country || "United States",
        }}
        isPending={addEmergencyContactMutation.isPending || updateEmergencyContactMutation.isPending}
      />
    </div>
  );
}
